import { useState } from 'react'
import css from './views.module.css'
import { Card, Button, MicroLabel } from '../ui'

/**
 * Hand the item to a Claude Code session.
 *
 * Everything in the briefing already exists in `context` -- the project, the
 * check, the failing count, the sample rows, the run, the links. Anyone
 * retyping that would produce something worse, so the item carries it and the
 * queue stops being a list of things to feel bad about.
 *
 * The text is shown, not just copied. Copying something you cannot read is how
 * people stop trusting a button, and the briefing carries real row data that
 * someone may want to trim before it leaves the page.
 */
export function HandOff({ prompt }: { prompt: string }) {
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)

  async function copy() {
    setFailed(false)
    try {
      await navigator.clipboard.writeText(prompt)
      setCopied(true)
      // Quiet, self-clearing confirmation. Not a toast, and nothing to dismiss.
      window.setTimeout(() => setCopied(false), 2500)
    } catch {
      // Clipboard access can be refused outright. Say so and open the text,
      // which is selectable, rather than pretending it worked.
      setFailed(true)
      setShown(true)
    }
  }

  return (
    <Card style={{ marginTop: 18 }}>
      <div className={css.section}>
        <div className={css.handOffHead}>
          <div>
            <MicroLabel>Hand to a session</MicroLabel>
            <div className={css.handOffNote}>
              A briefing assembled from this item — the findings, the samples and the links,
              already written out.
            </div>
          </div>
          <div className={css.handOffActions}>
            <Button variant="primary" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy prompt'}
            </Button>
            <Button variant="quiet" onClick={() => setShown((value) => !value)}>
              {shown ? 'Hide' : 'Show'}
            </Button>
          </div>
        </div>
        {failed ? (
          <div className={css.handOffNote} style={{ marginTop: 8 }}>
            The browser refused clipboard access. The text is below and can be selected.
          </div>
        ) : null}
        {shown ? <pre className={css.handOffText}>{prompt}</pre> : null}
      </div>
    </Card>
  )
}
